import { quadtree } from 'd3-quadtree';
import type { Point } from 'framer-motion';

import { DEFAULT_DATA_POINT_RADIUS } from '../../core/constants.js';
import { safeGet } from '../../core/utils/safe-get.js';
import type {
  AreaSeriesQueryResult,
  DotSeriesQueryResult,
  LineSeriesQueryResult,
  SeriesQuadtreeDatapoint,
  SeriesToQuadtree,
} from '../context/XYChartOverlayPerformance.context.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  type AnyLinearSeries,
  type AreaSeriesWithDatapoints,
  type AxisIdToScaleDomain,
  type DotSeriesWithDatapoints,
  type LineSeriesWithDatapoints,
  type SeriesToMetadata,
  type MapDatapoints,
} from '../types/xy-chart-internals.js';
import {
  getLinearAccessors,
  type AnyLinearSeriesMetadata,
} from './get-linear-accessors.js';
import { getCurrentScale } from './get-scale.js';
import { relAccessor } from './value-representation.js';

/**
 * Get axis accessors for a given series based on its type.
 *
 * @param series - The series to get accessors for.
 * @param metadata - Series metadata.
 * @param xScale - X scale.
 * @param yScale - Y scale.
 * @param axisIdToScaleDomain - Domain lookup keyed by axis id.
 * @param datapointToMetadata - Map with metadata (e.g., gap flags).
 * @returns Tuple of [xAccessor, yAccessor] or undefined if accessor pair not found.
 */
function getAxisAccessors(
  series: AnyLinearSeries,
  metadata: AnyLinearSeriesMetadata,
  xScale: ReturnType<typeof getCurrentScale>,
  yScale: ReturnType<typeof getCurrentScale>,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointToMetadata: MapDatapoints,
) {
  const { xAxis, yAxis } = metadata;
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const { xAccessor, yAccessor } = getLinearAccessors(
    series.type,
    metadata,
    datapointToMetadata,
    xRelAccessor,
    yRelAccessor,
  );

  return [xAccessor(xScale), yAccessor(yScale)];
}

/**
 * Filter datapoints to include only those within range and not marked as gaps.
 *
 * @param points - Array of datapoints to filter.
 * @param xAxisAccessor - Function to get x value from datapoint.
 * @param yAxisAccessor - Function to get y value from datapoint.
 * @param bounds - Object containing xMin, xMax, yMin, yMax boundaries.
 * @param datapointToMetadata - Map with metadata (e.g., gap flags).
 * @returns Filtered array of datapoints.
 */
function filterDatapointsInRange(
  points: SeriesQuadtreeDatapoint[],
  xAxisAccessor: (d: SeriesQuadtreeDatapoint) => number,
  yAxisAccessor: (d: SeriesQuadtreeDatapoint) => number,
  bounds: { xMin: number; xMax: number; yMin: number; yMax: number },
  datapointToMetadata: MapDatapoints,
): SeriesQuadtreeDatapoint[] {
  const { xMin, xMax, yMin, yMax } = bounds;

  return points.filter((point) => {
    if (safeGet(datapointToMetadata, point).isGap) {
      return false;
    }

    const xValue = xAxisAccessor(point);
    const yValue = yAxisAccessor(point);

    if (xValue < xMin || xValue > xMax) {
      return false;
    }

    if (yValue < yMin || yValue > yMax) {
      return false;
    }

    return true;
  });
}

/**
 * Build a quadtree for a single series.
 *
 * @param series - The series to build a quadtree for.
 * @param xAxisAccessor - Function to get x value from datapoint.
 * @param yAxisAccessor - Function to get y value from datapoint.
 * @param filtered - Filtered array of datapoints.
 * @returns The constructed quadtree.
 */
function buildSingleQuadtree(
  xAxisAccessor: (d: SeriesQuadtreeDatapoint) => number,
  yAxisAccessor: (d: SeriesQuadtreeDatapoint) => number,
  filtered: SeriesQuadtreeDatapoint[],
) {
  return quadtree<SeriesQuadtreeDatapoint>()
    .x((d) => xAxisAccessor(d))
    .y((d) => yAxisAccessor(d))
    .addAll(filtered);
}

/**
 * Build quadtrees for fast nearest-point queries for each linear series.
 * Filters out gap datapoints and points outside current x/y range.
 *
 * @param seriesWithDatapoints - Array of linear (dot/line/area) series.
 * @param axisIdToScales - Scale lookup keyed by axis id.
 * @param datapointToMetadata - Map with metadata (e.g., gap flags).
 * @param seriesToMetadata - Map from series to its metadata.
 * @param axisIdToScaleDomain - Domain lookup keyed by axis id.
 * @returns Map from series id to its quadtree.
 */
export function buildQuadtrees(
  seriesWithDatapoints: AnyLinearSeries[],
  axisIdToScales: AxisIdToScales,
  datapointToMetadata: MapDatapoints,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): SeriesToQuadtree {
  const seriesToTrees: SeriesToQuadtree = new Map();

  for (const series of seriesWithDatapoints) {
    const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
    const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
    const metadata = safeGet(seriesToMetadata, series);

    const accessors = getAxisAccessors(
      series,
      metadata,
      xScale,
      yScale,
      axisIdToScaleDomain,
      datapointToMetadata,
    );

    const [xAxisAccessor, yAxisAccessor] = accessors;

    const [xMin, xMax] = xScale.range();
    const [yMin, yMax] = yScale.range();

    const filtered = filterDatapointsInRange(
      series.data,
      xAxisAccessor,
      yAxisAccessor,
      {
        xMin: Math.min(xMin, xMax),
        xMax: Math.max(xMin, xMax),
        yMin: Math.min(yMin, yMax),
        yMax: Math.max(yMin, yMax),
      },
      datapointToMetadata,
    );

    const tree = buildSingleQuadtree(xAxisAccessor, yAxisAccessor, filtered);

    seriesToTrees.set(series.id, tree);
  }

  return seriesToTrees;
}

/**
 * Euclidean distance between two (x,y) points.
 *
 * @param x0 - First point x.
 * @param x1 - Second point x.
 * @param y0 - First point y.
 * @param y1 - Second point y.
 * @returns Distance value.
 */
export function distanceBetweenTwoPoints(
  x0: number,
  x1: number,
  y0: number,
  y1: number,
) {
  return Math.sqrt(Math.pow(x0 - x1, 2) + Math.pow(y0 - y1, 2));
}

/**
 * Get axis values for a datapoint based on series type.
 *
 * @param datapoint - The datapoint to extract values from.
 * @param series - The series the datapoint belongs to.
 * @param metadata - Series metadata.
 * @param xScale - X scale.
 * @param yScale - Y scale.
 * @param axisIdToScaleDomain - Domain lookup keyed by axis id.
 * @param datapointToMetadata - Map with metadata (e.g., gap flags).
 * @returns Tuple of [xValue, yValue].
 */
function getDatapointAxisValues(
  datapoint: SeriesQuadtreeDatapoint,
  series: AnyLinearSeries,
  metadata: AnyLinearSeriesMetadata,
  xScale: ReturnType<typeof getCurrentScale>,
  yScale: ReturnType<typeof getCurrentScale>,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointToMetadata: MapDatapoints,
): [number, number] {
  const accessors = getAxisAccessors(
    series,
    metadata,
    xScale,
    yScale,
    axisIdToScaleDomain,
    datapointToMetadata,
  );

  return [accessors[0](datapoint), accessors[1](datapoint)];
}

/**
 * Find the closest datapoint in a series to a given position.
 *
 * @param series - The series to search.
 * @param tree - The quadtree for the series.
 * @param position - The position to find closest datapoint to.
 * @param metadata - Series metadata.
 * @param queryContext - Object containing scales and metadata lookups.
 * @returns Object with distance and datapoint, or undefined if not found.
 */
function findClosestDatapointInSeries(
  series: AnyLinearSeries,
  tree: ReturnType<typeof quadtree<SeriesQuadtreeDatapoint>>,
  position: Point,
  metadata: AnyLinearSeriesMetadata,
  queryContext: {
    xScale: ReturnType<typeof getCurrentScale>;
    yScale: ReturnType<typeof getCurrentScale>;
    axisIdToScaleDomain: AxisIdToScaleDomain;
    datapointToMetadata: MapDatapoints;
  },
):
  | {
      type: string;
      distance: number;
      datapoint: SeriesQuadtreeDatapoint;
      series: AnyLinearSeries;
    }
  | undefined {
  const datapoint = tree.find(position.x, position.y);
  if (!datapoint) {
    return undefined;
  }

  if (!queryContext.datapointToMetadata.has(datapoint)) {
    return undefined;
  }

  const [xValue, yValue] = getDatapointAxisValues(
    datapoint,
    series,
    metadata,
    queryContext.xScale,
    queryContext.yScale,
    queryContext.axisIdToScaleDomain,
    queryContext.datapointToMetadata,
  );

  const distance = distanceBetweenTwoPoints(
    position.x,
    xValue,
    position.y,
    yValue,
  );

  return {
    type: series.type,
    distance,
    datapoint,
    series,
  };
}

/**
 * Build a query function that returns the closest datapoint
 * among provided series to a given pixel position.
 * Stops early if within DEFAULT_DATA_POINT_RADIUS.
 *
 * @param seriesIdToQuadtree - Map of series id to quadtree.
 * @param axisIdToScales - Axis scale lookup.
 * @returns QueryClosest function implementation.
 */
export function buildQueryClosest(
  seriesIdToQuadtree: SeriesToQuadtree,
  axisIdToScales: AxisIdToScales,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  datapointToMetadata: MapDatapoints,
) {
  function query(
    arrayOfSeries: DotSeriesWithDatapoints[],
    position: Point,
  ): DotSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: LineSeriesWithDatapoints[],
    position: Point,
  ): LineSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: AreaSeriesWithDatapoints[],
    position: Point,
  ): AreaSeriesQueryResult | undefined;
  function query(arrayOfSeries: AnyLinearSeries[], position: Point) {
    let best;

    for (const series of arrayOfSeries) {
      const tree = seriesIdToQuadtree.get(series.id);
      if (!tree) {
        continue;
      }

      const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
      const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
      const metadata = safeGet(seriesToMetadata, series);

      const closest = findClosestDatapointInSeries(
        series,
        tree,
        position,
        metadata,
        {
          xScale,
          yScale,
          axisIdToScaleDomain,
          datapointToMetadata,
        },
      );

      if (!closest) {
        continue;
      }

      if (!best || closest.distance < best.distance) {
        best = closest;
      }

      if (best && best.distance <= DEFAULT_DATA_POINT_RADIUS) {
        break;
      }
    }

    return best;
  }

  return query;
}
